import React from "react";
import { Business } from "@norma-bi/bi-api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useT } from "@/i18n";

export type RenameDialogProps = {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  business: Business;
  onConfirm: (newDisplayName: string) => void;
  isConfirmPending?: boolean;
};

export function RenameDialog({
  show,
  setShow,
  business,
  onConfirm,
  isConfirmPending = false,
}: RenameDialogProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const { t } = useT();
  const [displayName, setDisplayName] = React.useState(business.display_name);

  React.useEffect(() => {
    setDisplayName(business.display_name);
  }, [business]);

  const isDisabled =
    displayName.trim() === "" || displayName === business.display_name || isConfirmPending;

  return (
    <Dialog open={show} onOpenChange={(open) => !open && setShow(false)}>
      <DialogContent
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!isDisabled) onConfirm(displayName);
          }}
        >
          <DialogHeader>
            <DialogTitle dir="auto" className="truncate pe-6">
              {business.display_name}
            </DialogTitle>
            <DialogDescription className="sr-only">{business.name}</DialogDescription>
          </DialogHeader>
          <div className="px-4 py-4">
            <Field>
              <FieldLabel htmlFor="displayName">{t("businesses.displayName")}</FieldLabel>
              <Input
                ref={inputRef}
                id="displayName"
                type="text"
                dir="auto"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={t("businesses.enterDisplayName")}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isDisabled}>
              {t("common.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
